// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDBadge from "theme/components/MDBadge";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import MDButton from "theme/components/MDButton";

import { useMaterialUIController } from "context";
import usePageTitle from "app/pageTitle";
import CompanyNotification from "app/company_notification";
import { useAuthUser } from "react-auth-kit";

function P2POrders() {
  const pageTitle = "FX Marketplace";
  usePageTitle(pageTitle);

  const auth = useAuthUser();
  const user = auth();

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const navigate = useNavigate();
  const [refreshPage, setRefreshPage] = useState(false);

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [orders, setOrders] = useState([]);

  const [showCompanyNotification, setShowCompanyNotification] = useState(false);
  const [companyNotification, setCompanyNotification] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("p2p/orders/ALL/ALL.json")
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setOrders(result.data.data.orders);

            if (result.data.notification) {
              setCompanyNotification(result.data.notification);
              if (result.data.notification.pages.includes("ALL")) {
                setShowCompanyNotification(true);
              } else if (result.data.notification.pages.includes("p2p-orders")) {
                setShowCompanyNotification(true);
              }
            }
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred. Make sure you have an active internet connection.");
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  function formatAmount(amount, currency) {
    const formatter = new Intl.NumberFormat(currency, {
      style: "currency",
      currency: `${currency}`,

      // These options are needed to round to whole numbers if that's what you want.
      // minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
      maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
    });

    return formatter.format(amount / 100);
  }

  const [currentOrder, setCurrentOrder] = useState(null);
  const [currentAmount, setCurrentAmount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);

  const handleOpenDialog = (p2pOrder) => {
    setCurrentOrder(p2pOrder);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setCurrentOrder(null);
    setCurrentAmount(0);
    setOpenDialog(false);
  };

  async function swap(reference, fundAmount) {
    setShowLoading(true);
    const json = { order_ref: `${reference}`, amount: fundAmount * 100 };
    await axios
      .post("p2p/orders/execute.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          // setSeverity("success");
          // setErrMsg(result.data.data.message);
          // setOpen(true);

          setRefreshPage(!refreshPage);
          navigate(`/p2p/fx/my-orders/${result.data.data.order.reference}`);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  function handleSwapSubmit(event) {
    event.preventDefault();
    if (currentOrder === null) {
      setSeverity("error");
      setErrMsg("Select a P2P order.");
      setOpen(true);
      return;
    }

    if (currentAmount <= 0) {
      setSeverity("error");
      setErrMsg("Enter a valid amount.");
      setOpen(true);
      return;
    }

    swap(currentOrder.reference, currentAmount);
    handleDialogClose();
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {companyNotification !== null && showCompanyNotification ? (
        <CompanyNotification
          title={companyNotification.title}
          description={companyNotification.description}
        />
      ) : (
        <> </>
      )}
      {currentOrder !== null ? (
        <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth="xs">
          <form onSubmit={handleSwapSubmit}>
            <DialogTitle>
              <MDBox>{currentOrder.side === "SELL" ? "Buy" : "Sell"} Swap</MDBox>
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                <MDBox>
                  How much {currentOrder.currency_pair.slice(0, 3)}
                  {" would you like to "}
                  {currentOrder.side === "BUY" ? "sell to" : "buy from"} this person?
                </MDBox>
              </DialogContentText>
              <MDBox sx={{ marginTop: "15px" }}>
                <MDInput
                  name="amount"
                  margin="dense"
                  label={`${currentOrder.currency_pair.slice(0, 3)} Amount`}
                  type="decimal"
                  fullWidth
                  variant="outlined"
                  onChange={(e) => setCurrentAmount(e.target.value)}
                />
              </MDBox>
            </DialogContent>
            <DialogActions>
              <MDButton onClick={handleDialogClose} color="warning" variant="outlined">
                Cancel
              </MDButton>
              <MDButton color="success" variant="outlined" type="submit">
                {currentOrder.side === "BUY" ? "Sell" : "Buy"}{" "}
                {currentOrder.currency_pair.slice(0, 3)}
              </MDButton>
            </DialogActions>
          </form>
        </Dialog>
      ) : (
        <br />
      )}
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <MDBox sx={{ marginBottom: "40px" }}>
                  <MDBox display="flex" alignItems="center" justifyContent="space-between">
                    <MDBox>
                      <MDButton
                        variant="link"
                        color={darkMode ? "white" : "dark"}
                        onClick={() => {
                          navigate("/p2p/fx/post");
                        }}
                      >
                        <Icon>add</Icon>&nbsp; Post Order
                      </MDButton>
                    </MDBox>
                    <MDBox>
                      <MDButton
                        variant="link"
                        color={darkMode ? "white" : "dark"}
                        onClick={() => {
                          navigate("/p2p/fx/my-orders");
                        }}
                      >
                        My Orders
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>
            <Grid container spacing={6}>
              {orders.length <= 0 ? (
                <Grid item xs={12} md={12} lg={12}>
                  <Card>
                    <MDBox pt={3} pb={2} px={2}>
                      <MDBox
                        component="ul"
                        display="flex"
                        flexDirection="column"
                        p={0}
                        m={0}
                        sx={{ listStyle: "none" }}
                      >
                        <small>No available orders found.</small>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Grid>
              ) : (
                <>
                  {orders.map((order) => (
                    <Grid item xs={12} md={4} lg={4}>
                      <Card>
                        <MDBox
                          component="li"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          bgColor={darkMode ? "transparent" : "grey-100"}
                          borderRadius="lg"
                          p={3}
                          mb={1}
                          mt={0}
                        >
                          <MDBox width="100%" display="flex" flexDirection="column">
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems={{ xs: "flex-start", sm: "center" }}
                              flexDirection={{ xs: "column", sm: "row" }}
                            >
                              <Grid container spacing={3}>
                                <Grid item xs={6} lg={6} md={6}>
                                  <MDBox>
                                    <MDBox>
                                      <MDBadge
                                        badgeContent={order.side}
                                        variant="contained"
                                        container
                                        color={order.side === "SELL" ? "error" : "success"}
                                      />
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={6} lg={6} md={6}>
                                  <MDBox display="flex" justifyContent="flex-end">
                                    <MDTypography variant="h6" fontWeight="medium">
                                      {order.side === "BUY"
                                        ? `${order.currency_pair.slice(
                                            -3
                                          )} to ${order.currency_pair.slice(0, 3)}`
                                        : `${order.currency_pair.slice(
                                            0,
                                            3
                                          )} to ${order.currency_pair.slice(-3)}`}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </MDBox>
                            <Divider />
                            <MDBox
                              display="flex"
                              flexDirection="row"
                              justifyContent="space-between"
                            >
                              <MDBox>
                                <MDTypography variant="caption" color="text">
                                  Available Amount:
                                </MDTypography>
                              </MDBox>
                              <MDBox>
                                <MDTypography
                                  variant="caption"
                                  fontWeight="medium"
                                  textTransform="capitalize"
                                >
                                  {formatAmount(order.liquidity, order.currency_pair.slice(0, 3))}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <MDBox
                              lineHeight={2}
                              display="flex"
                              flexDirection="row"
                              justifyContent="space-between"
                            >
                              <MDBox>
                                <MDTypography variant="caption" color="text">
                                  Minimum Amount:
                                </MDTypography>
                              </MDBox>
                              <MDBox>
                                <MDTypography
                                  variant="caption"
                                  fontWeight="medium"
                                  textTransform="capitalize"
                                >
                                  {order.allow_partial_fill
                                    ? formatAmount(
                                        order.min_transaction_amount,
                                        order.currency_pair.slice(0, 3)
                                      )
                                    : formatAmount(
                                        order.liquidity,
                                        order.currency_pair.slice(0, 3)
                                      )}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <MDBox
                              lineHeight={2}
                              display="flex"
                              flexDirection="row"
                              justifyContent="space-between"
                            >
                              <MDBox>
                                <MDTypography variant="caption" color="text">
                                  Exchange Rate:
                                </MDTypography>
                              </MDBox>
                              <MDBox>
                                <MDTypography
                                  variant="caption"
                                  fontWeight="medium"
                                  textTransform="capitalize"
                                >
                                  {order.side === "SELL"
                                    ? formatAmount(
                                        order.limit_sell_rate,
                                        order.currency_pair.slice(-3)
                                      )
                                    : formatAmount(
                                        order.limit_buy_rate,
                                        order.currency_pair.slice(-3)
                                      )}{" "}
                                  / {formatAmount(100, order.currency_pair.slice(0, 3))}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <MDBox mt={5}>
                              {user.account_identifier === order.user ? (
                                <MDButton
                                  disabled
                                  size="small"
                                  color="dark"
                                  variant="outlined"
                                  sx={{ width: "100%" }}
                                >
                                  Self Trade
                                </MDButton>
                              ) : (
                                <MDButton
                                  size="small"
                                  variant="contained"
                                  color="info"
                                  sx={{ width: "100%" }}
                                  onClick={() => {
                                    handleOpenDialog(order);
                                  }}
                                >
                                  <Icon>currency_exchange</Icon>
                                  &nbsp;Accept Swap
                                </MDButton>
                              )}
                            </MDBox>
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default P2POrders;
